import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { usePnlHistory } from '@/hooks/queries/useAnalytics';
import { formatCurrency, formatShortDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { PERIODS, type Period } from './periods';

export type ChartType = 'cumulative' | 'daily';

export const CHART_TYPES: readonly ChartType[] = ['cumulative', 'daily'];

export interface PnlHistoryChartProps {
  period: Period;
  onPeriodChange: (period: Period) => void;
  chartType: ChartType;
  onChartTypeChange: (chartType: ChartType) => void;
}

/**
 * Props-driven: the page owns period and chart type so they live in the URL, and so this widget
 * and the premium flow below it move together on one `?period=`.
 */
export function PnlHistoryChart({ period, onPeriodChange, chartType, onChartTypeChange }: PnlHistoryChartProps) {
  const { data: dataPoints, isLoading } = usePnlHistory(period);

  return (
    <Card data-testid="pnl-history">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <CardTitle>P&L History</CardTitle>
        <div className="flex gap-4">
          <div className="flex gap-1">
            <button
              onClick={() => onChartTypeChange('cumulative')}
              aria-pressed={chartType === 'cumulative'}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded transition-colors',
                chartType === 'cumulative'
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              Cumulative
            </button>
            <button
              onClick={() => onChartTypeChange('daily')}
              aria-pressed={chartType === 'daily'}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded transition-colors',
                chartType === 'daily'
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              Daily
            </button>
          </div>
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => onPeriodChange(p.value)}
                aria-pressed={period === p.value}
                className={cn(
                  'px-2 py-1 text-xs font-medium rounded transition-colors',
                  period === p.value
                    ? 'bg-gray-200 text-gray-900'
                    : 'text-gray-500 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-80">
            <Spinner />
          </div>
        ) : !dataPoints || dataPoints.length === 0 ? (
          <div className="h-80 flex items-center justify-center">
            <EmptyState
              icon={<NoDataIcon />}
              title="No P&L data"
              description="Close some trades to see your performance history"
            />
          </div>
        ) : chartType === 'cumulative' ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dataPoints} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <defs>
                  <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => formatShortDate(date)}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value).replace('.00', '')}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
                <Area
                  type="monotone"
                  dataKey="cumulativePnl"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  fill="url(#colorPnl)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dataPoints} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => formatShortDate(date)}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value).replace('.00', '')}
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="#9ca3af" />
                <Bar
                  dataKey="realizedPnl"
                  fill="#0ea5e9"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;

  return (
    <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
      <p className="text-sm text-gray-500">{formatShortDate(label)}</p>
      <p className="text-sm font-medium">
        Daily:{' '}
        <span className={data.realizedPnl >= 0 ? 'text-green-600' : 'text-red-600'}>
          {formatCurrency(data.realizedPnl)}
        </span>
      </p>
      <p className="text-sm font-medium">
        Cumulative:{' '}
        <span className={data.cumulativePnl >= 0 ? 'text-green-600' : 'text-red-600'}>
          {formatCurrency(data.cumulativePnl)}
        </span>
      </p>
    </div>
  );
}
